import React from 'react';
import { computed, observable, makeObservable } from 'mobx';
import { MarketHeader } from 'src/domains/sportsbook/webview/components/marketHeader/MarketHeader';
import { getEachWayTerms } from 'src/domains/sportsbook/webview/components/marketHeader/getEachWayTerms';
import { SelectionGroup } from 'src/domains/sportsbook/webview/components/selectionsGroup/SelectionGroup';
import { SelectionGroupUnified } from 'src/domains/sportsbook/webview/components/selectionsGroupUnified/SelectionGroupUnified';
import { GoalscorerMarketGroup } from 'src/domains/sportsbook/webview/components/goalscorerMarketGroup/GoalscorerMarketGroup';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { observer } from 'src/utils/mobx-react';
import { MarketFiltersForSportState } from 'src/domains/sportsbook/state/marketFiltersState/MarketFiltersForSportState';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { sortSelectionsByCriteria } from 'src_common/utils/sport/sort';
import { OutrightMarket } from 'src/domains/sportsbook/webview/components/eventMarkets/marketGroup/outrightMarket/OutrightMarket';
import { Common, useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { MarketGroupWrapper } from './MarketGroups.style';
import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { TotalGoalsMarketGroup } from '../../marketGroups/totalGoalsMarketGroup/TotalGoalsMarketGroup';
import { CorrectScoreMarketGroup } from 'src/domains/sportsbook/webview/components/marketGroups/correctScoreMarketGroup/CorrectScoreMarketGroup';
import { MatchWinnerMarketGroup } from 'src/domains/sportsbook/webview/components/marketGroups/matchWinnerMarketGroup/MatchWinnerMarketGroup';
import { ExactHalfCornersMarketGroup } from 'src/domains/sportsbook/webview/components/marketGroups/exactHalfCornersMarketGroup/ExactHalfCornersMarketGroup';
import { MatchCornersCornersMarketGroup } from 'src/domains/sportsbook/webview/components/marketGroups/matchCornersMarketGroup/MatchCornersMarketGroup';
import { GoalscorerMarketGroupUnified } from 'src/domains/sportsbook/webview/components/goalscorerMarketGroupUnified/GoalscorerMarketGroupUnified';
import { OutrightMarketUnified } from 'src/domains/sportsbook/webview/components/eventMarkets/marketGroup/outrightMarket/OutrightMarketUnified';
import { LineGroupedMarketGroup } from 'src/domains/sportsbook/webview/components/marketGroups/lineGroupedMarketGroup/LineGroupedMarketGroup';
import {
    ComboOverUnderLineMarketGroup,
    shouldRenderComboOverUnderStack,
} from 'src/domains/sportsbook/webview/components/marketGroups/comboOverUnderLineMarketGroup/ComboOverUnderLineMarketGroup';
import { GolfSelections } from 'src/domains/sportsbook/webview/components/sportEventsListNew/SportCompetitionsList/golf/GolfSelections';

// Define placeholders and their replacements
// otherwise the match will be replaced with an empty string
const PLACEHOLDERS = (match: string, getTranslation: (key: string, defaultText: string) => string): string => {
    switch (match) {
        case '${home}':
            return getTranslation('events.market-group.placeholders.home', 'Home');
        case '${away}':
            return getTranslation('events.market-group.placeholders.away', 'Away');
        case '${homesubparticipant}':
            return getTranslation('events.market-group.placeholders.home-subparticipant', 'Home Subparticipant');
        case '${awaysubparticipant}':
            return getTranslation('events.market-group.placeholders.away-subparticipant', 'Away Subparticipant');
        case '${player}':
            return getTranslation('events.market-group.placeholders.player', 'Player');
        default:
            return '';
    }
};

interface StateType {
    name: string;
    templateId: string;
    marketsNew: Array<MarketId>;
    selections: Record<number | string, SelectionModel[]>;
    markets: Array<EventMarketItemType>;
}

function buildStateNew(markets: Array<EventMarketItemType>, common: Common, isOpen: boolean): StateType {
    const marketsList = markets;
    const mark = markets[0];
    const templateId = mark === undefined ? '' : mark.templateId;
    const languagesState = LanguagesState.get(common);

    // Replace any match placeholders with an alternative
    const name =
        mark === undefined
            ? ''
            : mark.tags['group-template'] === 'yes'
              ? mark.templateName.replace(/(\s\${line})|\${\w+(-)?\w+}/gi, (match) =>
                    PLACEHOLDERS(match, languagesState.getTranslation)
                )
              : mark.name;

    const marketsNew: Array<MarketId> = [];
    for (const market of markets) {
        marketsNew.push(market.id2);
    }

    const selections: Record<string, SelectionModel[]> = {};

    if (isOpen) {
        marketsList.forEach((market: EventMarketItemType) => {
            const fullMarket = AppSportsBookState.get(common).models.getMarket(market.id);
            if (fullMarket === null) {
                selections[market.id] = [];
                return;
            }
            const displayOrder = fullMarket.displayOrderTag;
            const order = displayOrder !== undefined && displayOrder !== '-' ? displayOrder : 'by-creation';
            const marketSelections = fullMarket.selections.filter((x) => x.shouldDisplay);
            selections[market.id] = sortSelectionsByCriteria(marketSelections, order);
        });
    }

    return {
        name,
        templateId,
        marketsNew,
        selections,
        markets,
    };
}

interface PropsType {
    type: string;
    eventId: EventId;
    markets: Array<EventMarketItemType>;
    order: number;
    suspendedMarketIds?: ReadonlySet<number>;
}

class MarketGroupState {
    @observable public isOpenInner: boolean | null = null;

    public constructor(
        private readonly order: number,
        private readonly marketFiltersForSportState: MarketFiltersForSportState | null
    ) {
        makeObservable(this);
    }

    @computed public get isOpen(): boolean {
        if (this.isOpenInner === null) {
            return this.isOpenOnStart;
        }

        return this.isOpenInner;
    }

    @computed public get isOpenOnStart(): boolean {
        if (this.marketFiltersForSportState !== null) {
            if (this.marketFiltersForSportState.activeFilter === 'popular') {
                return true;
            }
        }
        return this.order <= 4;
    }

    public setIsOpen = (): void => {
        if (this.isOpenInner === null) {
            this.isOpenInner = !this.isOpenOnStart;
        } else {
            this.isOpenInner = !this.isOpenInner;
        }
    };
}

export const MarketGroup = observer('MarketGroup', (props: PropsType) => {
    const common = useCommon();
    const appSportsBookState = AppSportsBookState.get(common);
    const { componentVariants, unifiedSportEventsTable } = ConfigComponents.get(common).unifiedComponentConfig;
    const { eventId, markets, order, type, suspendedMarketIds } = props;

    const event = eventId.getEventModel();
    if (event === null) {
        return null;
    }

    const marketFiltersForSportState = appSportsBookState.getMarketFiltersBySport(event.sport, eventId);

    const [marketGroupState] = React.useState(() => new MarketGroupState(order, marketFiltersForSportState));

    const state = buildStateNew(markets, common, marketGroupState.isOpen);
    const { marketsNew, selections, name } = state;

    const marketId = marketsNew[0] ?? null;
    const market = markets[0] ?? null;

    if (market === null) {
        return null;
    }

    if (marketId === null) {
        return null;
    }

    if (market.display === false) {
        return null;
    }

    // if (market.state !== 'open') {
    //     return null;
    // }

    const marketDisplayTemplate = market.tags['display-template'];

    // Redirect to special components only if no market display template was chosen or is a goalscorer
    const specialMarketType =
        marketDisplayTemplate !== undefined && /goalscorer/.exec(marketDisplayTemplate) !== null ? 'goalscorer' : type;

    const headerName = market.name;

    const isOutrights = event.template === 'outrights' || specialMarketType === 'custom-outrights-market';

    if (specialMarketType === 'goalscorer' || specialMarketType.startsWith('goalscorer-standalone')) {
        if (unifiedSportEventsTable) {
            return (
                <GoalscorerMarketGroupUnified
                    eventId={eventId}
                    marketId={marketId}
                    selections={selections}
                    isOpen={marketGroupState.isOpen}
                    onClick={marketGroupState.setIsOpen}
                    headerName={markets.length === 1 ? headerName : 'First Goalscorer'}
                />
            );
        }
        return (
            <GoalscorerMarketGroup
                eventId={eventId}
                markets={marketsNew}
                selections={selections}
                isOpen={marketGroupState.isOpen}
                onClick={marketGroupState.setIsOpen}
            />
        );
    } else if (unifiedSportEventsTable === false && specialMarketType === 'custom-outrights-market') {
        return (
            <OutrightMarket
                markets={marketsNew}
                selections={selections}
                isOpen={marketGroupState.isOpen}
                onClick={marketGroupState.setIsOpen}
            />
        );
    } else if (unifiedSportEventsTable && event.sport !== 'golf' && isOutrights) {
        return (
            <OutrightMarketUnified
                eventId={eventId}
                markets={marketsNew}
                selections={selections}
                isOpen={marketGroupState.isOpen}
                onClick={marketGroupState.setIsOpen}
                displayTemplate={marketDisplayTemplate ?? ''}
            />
        );
    } else if (unifiedSportEventsTable && specialMarketType.startsWith('line-grouped:')) {
        const lineGroupedHeaderName = specialMarketType.slice('line-grouped:'.length) || name;
        const LineGroupComponent = shouldRenderComboOverUnderStack(marketsNew, selections)
            ? ComboOverUnderLineMarketGroup
            : LineGroupedMarketGroup;

        return (
            <LineGroupComponent
                name={lineGroupedHeaderName}
                marketId={marketId}
                markets={marketsNew}
                marketItems={markets}
                selections={selections}
                isOpen={marketGroupState.isOpen}
                onClick={marketGroupState.setIsOpen}
                suspendedMarketIds={suspendedMarketIds}
            />
        );
    } else if (
        (unifiedSportEventsTable && specialMarketType.includes('total-goals')) ||
        specialMarketType.includes('total-half-goals')
    ) {
        return (
            <TotalGoalsMarketGroup
                marketId={marketId}
                markets={marketsNew}
                selections={selections}
                isOpen={marketGroupState.isOpen}
                onClick={marketGroupState.setIsOpen}
                templateName={market.templateName || 'Total Goals'}
            />
        );
    } else if (
        unifiedSportEventsTable &&
        (specialMarketType.includes('correct-score') || /correct score/i.test(market.name))
    ) {
        const marketModel = marketId.getModel();
        if (marketModel === null) {
            return null;
        }
        return (
            <CorrectScoreMarketGroup
                marketId={marketId}
                templateName={market.name || market.templateName || 'Correct Score'}
                market={marketModel}
                eventId={eventId}
                markets={marketsNew}
                selections={selections}
                isOpen={marketGroupState.isOpen}
                onClick={marketGroupState.setIsOpen}
            />
        );
    } else if (unifiedSportEventsTable && specialMarketType.includes('match-winner-goals')) {
        return (
            <MatchWinnerMarketGroup
                eventId={eventId}
                markets={marketsNew}
                selections={selections}
                isOpen={marketGroupState.isOpen}
                onClick={marketGroupState.setIsOpen}
            />
        );
    } else if (unifiedSportEventsTable && specialMarketType.match(/exact-\d[a-z]{2}-half-corners/)) {
        return (
            <ExactHalfCornersMarketGroup
                markets={marketsNew}
                selections={selections}
                isOpen={marketGroupState.isOpen}
                onClick={marketGroupState.setIsOpen}
            />
        );
    } else if (unifiedSportEventsTable && specialMarketType === 'match-corners') {
        return (
            <MatchCornersCornersMarketGroup
                markets={marketsNew}
                selections={selections}
                isOpen={marketGroupState.isOpen}
                onClick={marketGroupState.setIsOpen}
            />
        );
    }

    const marketModel = marketId.getModel();

    return (
        <MarketGroupWrapper
            variant={componentVariants.eventRowVariant}
            data-test='market-group'
        >
            <MarketHeader
                name={name}
                isOpen={marketGroupState.isOpen}
                onClick={marketGroupState.setIsOpen}
                marketId={market.id2}
                event={event}
                eachWayTerms={marketModel === null ? undefined : getEachWayTerms(marketModel)}
            />
            {marketGroupState.isOpen &&
                (unifiedSportEventsTable ? (
                    event.sport === 'golf' ? (
                        <GolfSelections
                            smallEventId={event.id2}
                            marketId={market.id2}
                            isOutrights={isOutrights}
                        />
                    ) : (
                        <SelectionGroupUnified
                            eventId={eventId}
                            marketIdMain={market.id2}
                            selections={selections}
                        />
                    )
                ) : (
                    <SelectionGroup
                        eventId={eventId}
                        marketIdMain={market.id2}
                        selections={selections}
                    />
                ))}
        </MarketGroupWrapper>
    );
});
